/**
 * Audit Persistence
 * Persists audit logs to storage for compliance and debugging
 */

import { ScriptAuditEntry } from '../handlers/ScriptExecutor';

export interface IAuditStorage {
  /**
   * Save audit entry to persistent storage
   */
  save(entry: ScriptAuditEntry): Promise<void>;

  /**
   * Retrieve audit entries with optional filtering
   */
  query(filter?: {
    startTime?: number;
    endTime?: number;
    scriptId?: string;
    allowedOnly?: boolean;
    blockedOnly?: boolean;
    limit?: number;
  }): Promise<ScriptAuditEntry[]>;

  /**
   * Delete entries older than specified timestamp
   */
  deleteOlderThan(timestamp: number): Promise<number>;

  /**
   * Get total entry count
   */
  count(): Promise<number>;
}

/**
 * In-memory audit storage (default, no persistence)
 */
export class InMemoryAuditStorage implements IAuditStorage {
  private entries: ScriptAuditEntry[] = [];
  private readonly MAX_ENTRIES = 10000;
  private truncationWarningThreshold = 9500; // Warn when 95% full

  async save(entry: ScriptAuditEntry): Promise<void> {
    this.entries.push(entry);

    // Warn when approaching max entries
    if (this.entries.length >= this.truncationWarningThreshold) {
      console.warn(
        `[AuditPersistence] Warning: Audit log is ${Math.round(
          (this.entries.length / this.MAX_ENTRIES) * 100
        )}% full (${this.entries.length}/${this.MAX_ENTRIES} entries)`
      );
    }

    // Keep only last MAX_ENTRIES entries with notification
    if (this.entries.length > this.MAX_ENTRIES) {
      const deletedCount = this.entries.length - this.MAX_ENTRIES;
      const oldestDeletedTime = this.entries[0].timestamp;

      this.entries = this.entries.slice(-this.MAX_ENTRIES);

      console.warn(
        `[AuditPersistence] Truncated audit log: Deleted ${deletedCount} entries (oldest: ${new Date(
          oldestDeletedTime
        ).toISOString()}). Keep entries by using persistent storage (LocalStorage or database).`
      );
    }
  }

  async query(filter?: {
    startTime?: number;
    endTime?: number;
    scriptId?: string;
    allowedOnly?: boolean;
    blockedOnly?: boolean;
    limit?: number;
  }): Promise<ScriptAuditEntry[]> {
    let results = [...this.entries];

    if (filter?.startTime) {
      results = results.filter((e) => e.timestamp >= filter.startTime!);
    }

    if (filter?.endTime) {
      results = results.filter((e) => e.timestamp <= filter.endTime!);
    }

    if (filter?.scriptId) {
      results = results.filter((e) => e.scriptId === filter.scriptId);
    }

    if (filter?.allowedOnly) {
      results = results.filter((e) => e.allowed);
    }

    if (filter?.blockedOnly) {
      results = results.filter((e) => !e.allowed);
    }

    if (filter?.limit) {
      results = results.slice(-filter.limit);
    }

    return results;
  }

  async deleteOlderThan(timestamp: number): Promise<number> {
    const beforeLength = this.entries.length;
    this.entries = this.entries.filter((e) => e.timestamp >= timestamp);
    return beforeLength - this.entries.length;
  }

  async count(): Promise<number> {
    return this.entries.length;
  }
}

/**
 * LocalStorage-based audit storage (browser only)
 */
export class LocalStorageAuditStorage implements IAuditStorage {
  private storageKey: string;
  private maxEntries: number;
  private truncationWarningThreshold: number;

  constructor(storageKey = 'formControlAuditLog', maxEntries = 5000) {
    this.storageKey = storageKey;
    this.maxEntries = maxEntries;
    this.truncationWarningThreshold = Math.floor(maxEntries * 0.95);
  }

  async save(entry: ScriptAuditEntry): Promise<void> {
    try {
      const stored = this.getAllEntries();
      stored.push(entry);

      // Warn when approaching max entries
      if (stored.length >= this.truncationWarningThreshold) {
        console.warn(
          `[AuditPersistence] Warning: Audit log is ${Math.round(
            (stored.length / this.maxEntries) * 100
          )}% full (${stored.length}/${this.maxEntries} entries). Consider persisting to database.`
        );
      }

      // Keep only last N entries with notification
      if (stored.length > this.maxEntries) {
        const deletedCount = stored.length - this.maxEntries;
        const oldestDeletedTime = stored[0].timestamp;

        stored.splice(0, stored.length - this.maxEntries);

        console.warn(
          `[AuditPersistence] Truncated audit log in localStorage: Deleted ${deletedCount} entries (oldest: ${new Date(
            oldestDeletedTime
          ).toISOString()}). Max capacity: ${this.maxEntries} entries.`
        );
      }

      localStorage.setItem(this.storageKey, JSON.stringify(stored));
    } catch (e) {
      // Handle quota exceeded or other errors
      if (e instanceof Error && e.name === 'QuotaExceededError') {
        console.error(
          '[AuditPersistence] CRITICAL: localStorage quota exceeded. Clearing old entries to make space.'
        );
        // Clear half the entries and try again
        try {
          const stored = this.getAllEntries();
          if (stored.length > 0) {
            const half = Math.floor(stored.length / 2);
            const deletedCount = half;
            const oldestKeptTime = stored[half].timestamp;

            stored.splice(0, half);
            stored.push(entry);

            console.warn(
              `[AuditPersistence] Cleared ${deletedCount} old entries from localStorage. Kept entries from ${new Date(
                oldestKeptTime
              ).toISOString()} onward.`
            );

            localStorage.setItem(this.storageKey, JSON.stringify(stored));
          }
        } catch (retryError) {
          console.error('[AuditPersistence] CRITICAL: Failed to save audit entry even after clearing:', retryError);
        }
      } else {
        console.error('[AuditPersistence] Failed to save audit entry to localStorage:', e);
      }
    }
  }

  async query(filter?: {
    startTime?: number;
    endTime?: number;
    scriptId?: string;
    allowedOnly?: boolean;
    blockedOnly?: boolean;
    limit?: number;
  }): Promise<ScriptAuditEntry[]> {
    let results = this.getAllEntries();

    if (filter?.startTime) {
      results = results.filter((e) => e.timestamp >= filter.startTime!);
    }

    if (filter?.endTime) {
      results = results.filter((e) => e.timestamp <= filter.endTime!);
    }

    if (filter?.scriptId) {
      results = results.filter((e) => e.scriptId === filter.scriptId);
    }

    if (filter?.allowedOnly) {
      results = results.filter((e) => e.allowed);
    }

    if (filter?.blockedOnly) {
      results = results.filter((e) => !e.allowed);
    }

    if (filter?.limit) {
      results = results.slice(-filter.limit);
    }

    return results;
  }

  async deleteOlderThan(timestamp: number): Promise<number> {
    const stored = this.getAllEntries();
    const beforeLength = stored.length;
    const filtered = stored.filter((e) => e.timestamp >= timestamp);
    localStorage.setItem(this.storageKey, JSON.stringify(filtered));
    return beforeLength - filtered.length;
  }

  async count(): Promise<number> {
    return this.getAllEntries().length;
  }

  private getAllEntries(): ScriptAuditEntry[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.warn('Failed to read audit log from localStorage:', e);
      return [];
    }
  }

  /**
   * Clear all stored audit logs
   */
  clearAll(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.warn('Failed to clear audit log:', e);
    }
  }
}

/**
 * Composite audit storage supporting multiple backends
 */
export class CompositeAuditStorage implements IAuditStorage {
  private storages: IAuditStorage[];

  constructor(storages: IAuditStorage[]) {
    this.storages = storages;
  }

  async save(entry: ScriptAuditEntry): Promise<void> {
    await Promise.all(this.storages.map((s) => s.save(entry)));
  }

  async query(filter?: {
    startTime?: number;
    endTime?: number;
    scriptId?: string;
    allowedOnly?: boolean;
    blockedOnly?: boolean;
    limit?: number;
  }): Promise<ScriptAuditEntry[]> {
    // Return results from first storage
    return this.storages[0]?.query(filter) ?? [];
  }

  async deleteOlderThan(timestamp: number): Promise<number> {
    let totalDeleted = 0;
    for (const storage of this.storages) {
      totalDeleted += await storage.deleteOlderThan(timestamp);
    }
    return totalDeleted;
  }

  async count(): Promise<number> {
    return this.storages[0]?.count() ?? 0;
  }
}
